import { useSidebar } from '../../contexts/SidebarContext';
import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import KPICard from './components/KPICard';
import NetWorthChart from './components/NetWorthChart';
import SurplusDeficitHeatmap from './components/SurplusDeficitHeatmap';
import AssetAllocationChart from './components/AssetAllocationChart';
import AICoachingInsights from './components/AICoachingInsights';
import ScenarioControls from './components/ScenarioControls';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { formatCurrencyINR } from '../../utils/currency';
import { useUserData } from '../../hooks/useUserData';
import { useTour } from '../../contexts/TourContext';
import VideoModal from '../../components/ui/VideoModal';
import { getVideoForPage } from '../../config/videoConfig';
import DemoModeBanner from '../../components/demo/DemoModeBanner';

const WealthDashboard = () => {
  const { sidebarExpanded, toggleSidebar } = useSidebar();
  const [selectedScenario, setSelectedScenario] = useState('base');
  const [safetyMargin, setSafetyMargin] = useState(4);
  const [refreshing, setRefreshing] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  // Get user data from Firebase
  const { userData, loading, error } = useUserData();
  const { startDashboardTour, autoStartTour } = useTour();
  
  const videoConfig = getVideoForPage('wealth-dashboard');

  useEffect(() => {
    // Simulate data refresh
    const handleRefresh = () => {
      setRefreshing(true);
      setTimeout(() => setRefreshing(false), 2000);
    };

    // Auto-refresh every 5 minutes
    const interval = setInterval(handleRefresh, 300000);
    return () => clearInterval(interval);
  }, []);

  // Auto-start tour for first-time visitors
  useEffect(() => {
    if (!loading && userData) {
      autoStartTour('dashboard', startDashboardTour);
    }
  }, [loading, userData, autoStartTour, startDashboardTour]);

  const handleScenarioChange = (scenario) => {
    setSelectedScenario(scenario);
    // Trigger data recalculation based on scenario
  };

  const handleSafetyMarginChange = (margin) => {
    setSafetyMargin(margin);
    // Trigger safety margin recalculation
  };

  const handleExportPDF = () => {
    // Implement PDF export functionality
    console.log('Exporting PDF report...');
  };

  const handleExportExcel = () => {
    // Implement Excel export functionality
    console.log('Exporting Excel report...');
  };

  // Calculate net worth and financial metrics
  const financialMetrics = useMemo(() => {
    if (!userData) {
      return {
        totalAssets: 0,
        totalLiabilities: 0,
        netWorth: 0,
        assetCount: 0,
        debtCount: 0
      };
    }

    // Calculate total assets
    const totalAssets = (userData.portfolio || []).reduce((sum, asset) => {
      return sum + (asset.currentValue || 0);
    }, 0);

    // Calculate total liabilities (debts)
    const totalLiabilities = (userData.debts || []).reduce((sum, debt) => {
      return sum + (debt.outstanding || 0);
    }, 0);

    // Calculate net worth: Assets - Liabilities
    const netWorth = totalAssets - totalLiabilities;

    return {
      totalAssets,
      totalLiabilities,
      netWorth,
      assetCount: userData.portfolio?.length || 0,
      debtCount: userData.debts?.length || 0
    };
  }, [userData]);

  // Calculate retirement readiness score (0-100) based on 5 weighted pillars
  const calculateRetirementReadiness = () => {
    if (!userData?.setup?.currentAge || !userData?.setup?.retirementYear) {
      return 0;
    }
    
    let totalScore = 0;
    
    // 1. Corpus vs. Requirement (35% weight)
    const corpusVsRequirementScore = calculateCorpusVsRequirementScore();
    totalScore += corpusVsRequirementScore * 0.35;
    
    // 2. Cash Flow Sustainability (25% weight)
    const cashFlowScore = calculateCashFlowSustainabilityScore();
    totalScore += cashFlowScore * 0.25;
    
    // 3. Debt Risk (15% weight)
    const debtRiskScore = calculateDebtRiskScore();
    totalScore += debtRiskScore * 0.15;
    
    // 4. Risk Resilience (15% weight)
    const riskResilienceScore = calculateRiskResilienceScore();
    totalScore += riskResilienceScore * 0.15;
    
    // 5. Flexibility & Liquidity (10% weight)
    const liquidityScore = calculateLiquidityScore();
    totalScore += liquidityScore * 0.10;
    
    return Math.min(100, Math.max(0, Math.round(totalScore)));
  };

  // Helper function to calculate Corpus vs. Requirement score (0-100)
  const calculateCorpusVsRequirementScore = () => {
    const targetCorpus = 50000000; // ₹5 crore target (simplified)
    const currentCorpus = financialMetrics.netWorth;
    const percentage = targetCorpus > 0 ? (currentCorpus / targetCorpus) * 100 : 0;
    
    if (percentage >= 150) return 100; // 150%+ of requirement
    else if (percentage >= 120) return 90; // 120-150%
    else if (percentage >= 100) return 80; // 100-120%
    else if (percentage >= 80) return 70; // 80-100%
    else if (percentage >= 60) return 50; // 60-80%
    else if (percentage >= 40) return 30; // 40-60%
    else if (percentage >= 20) return 15; // 20-40%
    else return 5; // <20%
  };

  // Helper function to calculate Cash Flow Sustainability score (0-100)
  const calculateCashFlowSustainabilityScore = () => {
    const totalIncome = userData?.income?.reduce((sum, inc) => sum + (inc.monthlyAmount || 0), 0) || 0;
    const totalExpenses = userData?.expenses?.reduce((sum, exp) => sum + (exp.monthlyAmount || 0), 0) || 0;
    
    if (totalIncome === 0) return 0;
    
    const cashFlowRatio = totalIncome / totalExpenses;
    
    if (cashFlowRatio >= 2.0) return 100; // 200%+ coverage
    else if (cashFlowRatio >= 1.5) return 90; // 150-200%
    else if (cashFlowRatio >= 1.2) return 80; // 120-150%
    else if (cashFlowRatio >= 1.0) return 70; // 100-120%
    else if (cashFlowRatio >= 0.8) return 50; // 80-100%
    else if (cashFlowRatio >= 0.6) return 30; // 60-80%
    else if (cashFlowRatio >= 0.4) return 15; // 40-60%
    else return 5; // <40%
  };

  // Helper function to calculate Debt Risk score (0-100)
  const calculateDebtRiskScore = () => {
    const totalAssets = financialMetrics.totalAssets;
    const totalLiabilities = financialMetrics.totalLiabilities;
    
    if (totalAssets === 0) return 0;
    
    const debtToAssetRatio = totalLiabilities / totalAssets;
    
    if (debtToAssetRatio === 0) return 100; // No debt
    else if (debtToAssetRatio <= 0.1) return 90; // ≤10% debt
    else if (debtToAssetRatio <= 0.2) return 80; // 10-20%
    else if (debtToAssetRatio <= 0.3) return 70; // 20-30%
    else if (debtToAssetRatio <= 0.4) return 50; // 30-40%
    else if (debtToAssetRatio <= 0.5) return 30; // 40-50%
    else if (debtToAssetRatio <= 0.6) return 15; // 50-60%
    else return 5; // >60%
  };

  // Helper function to calculate Risk Resilience score (0-100)
  const calculateRiskResilienceScore = () => {
    const totalAssets = financialMetrics.totalAssets;
    const emergencyFund = userData?.expenses?.reduce((sum, exp) => sum + (exp.monthlyAmount || 0), 0) * 6 || 0; // 6 months expenses
    const liquidAssets = userData?.portfolio?.filter(asset => asset.liquidity === 'Liquid').reduce((sum, asset) => sum + (asset.currentValue || 0), 0) || 0;
    
    if (totalAssets === 0) return 0;
    
    const emergencyFundRatio = emergencyFund / totalAssets;
    const liquidAssetRatio = liquidAssets / totalAssets;
    
    // Combined resilience score
    let score = 0;
    
    // Emergency fund factor (50% of resilience score)
    if (emergencyFundRatio >= 0.3) score += 50; // 30%+ in emergency fund
    else if (emergencyFundRatio >= 0.2) score += 40; // 20-30%
    else if (emergencyFundRatio >= 0.1) score += 30; // 10-20%
    else if (emergencyFundRatio >= 0.05) score += 20; // 5-10%
    else score += 10; // <5%
    
    // Liquid assets factor (50% of resilience score)
    if (liquidAssetRatio >= 0.5) score += 50; // 50%+ liquid
    else if (liquidAssetRatio >= 0.3) score += 40; // 30-50%
    else if (liquidAssetRatio >= 0.2) score += 30; // 20-30%
    else if (liquidAssetRatio >= 0.1) score += 20; // 10-20%
    else score += 10; // <10%
    
    return score;
  };

  // Helper function to calculate Flexibility & Liquidity score (0-100)
  const calculateLiquidityScore = () => {
    const totalAssets = financialMetrics.totalAssets;
    if (totalAssets === 0) return 0;
    
    const liquidAssets = userData?.portfolio?.filter(asset => asset.liquidity === 'Liquid').reduce((sum, asset) => sum + (asset.currentValue || 0), 0) || 0;
    const semiLiquidAssets = userData?.portfolio?.filter(asset => asset.liquidity === 'Semi-Liquid').reduce((sum, asset) => sum + (asset.currentValue || 0), 0) || 0;
    
    const liquidRatio = liquidAssets / totalAssets;
    const semiLiquidRatio = semiLiquidAssets / totalAssets;
    
    // Weighted liquidity score
    let score = 0;
    
    // Liquid assets (70% weight)
    if (liquidRatio >= 0.4) score += 70; // 40%+ liquid
    else if (liquidRatio >= 0.3) score += 60; // 30-40%
    else if (liquidRatio >= 0.2) score += 50; // 20-30%
    else if (liquidRatio >= 0.1) score += 30; // 10-20%
    else score += 15; // <10%
    
    // Semi-liquid assets (30% weight)
    if (semiLiquidRatio >= 0.3) score += 30; // 30%+ semi-liquid
    else if (semiLiquidRatio >= 0.2) score += 25; // 20-30%
    else if (semiLiquidRatio >= 0.1) score += 20; // 10-20%
    else if (semiLiquidRatio >= 0.05) score += 15; // 5-10%
    else score += 10; // <5%
    
    return score;
  };

  // Calculate corpus vs requirement percentage
  const calculateCorpusVsRequirement = () => {
    // This is a simplified calculation - in a real app, you'd calculate based on retirement goals
    const targetCorpus = 50000000; // ₹5 crore target (simplified)
    const currentCorpus = financialMetrics.netWorth;
    const percentage = targetCorpus > 0 ? (currentCorpus / targetCorpus) * 100 : 0;
    return Math.min(200, Math.max(0, percentage)); // Cap at 200%
  };

  // Calculate net worth percentage change (comparing with previous month)
  const calculateNetWorthChange = () => {
    if (!userData?.portfolio || !userData?.debts) {
      return { percentage: 0, trend: 'neutral' };
    }

    // Calculate current net worth
    const currentNetWorth = financialMetrics.netWorth;
    
    // Calculate previous month's net worth (simulate with purchase values for assets)
    const previousAssets = (userData.portfolio || []).reduce((sum, asset) => {
      return sum + (asset.purchaseValue || asset.currentValue || 0);
    }, 0);
    
    const previousLiabilities = (userData.debts || []).reduce((sum, debt) => {
      // Assume debts were slightly higher last month (simplified)
      return sum + (debt.outstanding || 0) * 1.02; // 2% higher last month
    }, 0);
    
    const previousNetWorth = previousAssets - previousLiabilities;
    
    if (previousNetWorth === 0) {
      return { percentage: 0, trend: 'neutral' };
    }
    
    const percentageChange = ((currentNetWorth - previousNetWorth) / previousNetWorth) * 100;
    const trend = percentageChange > 0 ? 'up' : percentageChange < 0 ? 'down' : 'neutral';
    
    return {
      percentage: Math.abs(percentageChange),
      trend
    };
  };

  const retirementReadiness = calculateRetirementReadiness();
  const corpusVsRequirement = calculateCorpusVsRequirement();
  const netWorthChange = calculateNetWorthChange();
  const currentMonthYear = new Date().toLocaleString('en-IN', { month: 'long', year: 'numeric' });
  const safetyFactor = Number(userData?.setup?.marginOfSafety ?? 4) || 4;

  const kpiData = [
    {
      title: 'Current Net Worth',
      value: formatCurrencyINR(financialMetrics.netWorth, { compact: true }),
      subtitle: `As of ${currentMonthYear}`,
      icon: 'Wallet',
      trend: netWorthChange.trend,
      trendValue: netWorthChange.trend === 'neutral' ? '0%' : `${netWorthChange.trend === 'up' ? '+' : '-'}${netWorthChange.percentage.toFixed(1)}%`,
      color: 'primary',
      tooltip: 'Calculated as Total Assets minus Total Liabilities. Assets are summed from portfolio currentValue; liabilities from debts outstanding.'
    },
    {
      title: 'Retirement Readiness',
      value: `${Math.round(retirementReadiness)}/100`,
      subtitle: retirementReadiness >= 70 ? 'Above average score' : retirementReadiness >= 50 ? 'Average score' : 'Below average score',
      icon: 'Target',
      trend: 'up',
      trendValue: '+5 pts',
      color: 'success',
      tooltip: 'Weighted score across five pillars: Corpus vs Requirement (35%), Cash Flow Sustainability (25%), Debt Risk (15%), Risk Resilience (15%), and Flexibility & Liquidity (10%). Each pillar is scored 0–100 and combined.'
    },
    {
      title: 'Years to Retirement',
      value: userData?.setup?.yearsToRetirement ? userData.setup.yearsToRetirement.toString() : 'Not set',
      subtitle: 'Based on current plan',
      icon: 'Clock',
      trend: 'down',
      trendValue: '-0.5 yrs',
      color: 'warning',
      tooltip: 'Years to retirement based on current plan. Calculated as retirement year - current year.'
    },
    {
      title: 'Corpus vs Requirement',
      value: `${Math.round(corpusVsRequirement)}%`,
      subtitle: `${safetyFactor}x safety margin included`,
      icon: 'TrendingUp',
      trend: 'up',
      trendValue: '+8.2%',
      color: 'accent',
      tooltip: 'Percentage of current net worth relative to a fixed target corpus (₹5 Cr). Formula: (Net Worth / ₹5,00,00,000) × 100, capped at 200%.'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isExpanded={sidebarExpanded} 
        onToggle={toggleSidebar} 
      />
      
      <Header 
        sidebarExpanded={sidebarExpanded}
        onSidebarToggle={toggleSidebar}
      />

      <DemoModeBanner />

      <main 
        className={`
          transition-all duration-300 ease-spring pt-16 pb-20 lg:pb-8
          ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
        `}
      >
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
          <Breadcrumb />
          
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading financial data...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Data</h2>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button onClick={() => window.location.reload()}>
                  Retry
                </Button>
              </div>
            </div>
          )}

          {/* Main Content - Only show when not loading and no error */}
          {!loading && !error && (
            <>
              {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8" data-tour="dashboard-header">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Wealth Dashboard</h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                {`Comprehensive overview of your ${(userData?.setup?.projectionHorizon || 20)}-year retirement planning journey`}
              </p>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="outline" iconName="Video" iconPosition="left" onClick={() => setIsVideoOpen(true)} className="hidden sm:inline-flex">
                Learn
              </Button>
              <Button variant="outline" iconName="HelpCircle" iconPosition="left" onClick={startDashboardTour} className="hidden sm:inline-flex">
                Tour
              </Button>
              {/* PDF and Excel export buttons - temporarily disabled
              <div className="flex bg-muted rounded-lg p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleExportPDF}
                  className="px-2 sm:px-3 py-1"
                >
                  <Icon name="FileText" size={16} />
                  <span className="hidden sm:inline">PDF</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleExportExcel}
                  className="px-2 sm:px-3 py-1"
                >
                  <Icon name="FileSpreadsheet" size={16} />
                  <span className="hidden sm:inline">Excel</span>
                </Button>
              </div>
              */}
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8" data-tour="dashboard-kpis">
            {kpiData.map((kpi, index) => (
              <KPICard
                key={index}
                title={kpi.title}
                value={kpi.value}
                subtitle={kpi.subtitle}
                icon={kpi.icon}
                trend={kpi.trend}
                trendValue={kpi.trendValue}
                color={kpi.color}
                tooltip={kpi.tooltip}
              />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Net Worth Chart - Takes full width */}
            <div data-tour="dashboard-networth">
              <NetWorthChart />
            </div>
          </div>

          {/* Secondary Charts - full width stacked */}
          <div className="grid grid-cols-1 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div data-tour="dashboard-allocation">
              <AssetAllocationChart />
            </div>
            <div data-tour="dashboard-heatmap">
              <SurplusDeficitHeatmap />
            </div>
          </div>

          {/* Scenario Controls - temporarily disabled
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
            <div className="xl:col-span-2">
              <ScenarioControls 
                onScenarioChange={handleScenarioChange}
                onSafetyMarginChange={handleSafetyMarginChange}
              />
            </div>
          </div>
          */}
            </>
          )}
        </div>
      </main>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl={videoConfig?.url}
        title={videoConfig?.title}
      />
    </div>
  );
};

export default WealthDashboard;